// ============================================
// R14 (t46): INVITE ACCEPT PAGE
// ============================================
//
// Landing page for `/invite/:token`. R14 explicitly requires
// auth — anonymous users are bounced to /login and then back
// here via the ?redirect= query param. Once authenticated, we
// load the link preview (public) so we can render a friendly
// "You've been invited to Foo as Member" panel before the user
// hits Accept.

import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, LogIn, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";
import { useAuthStore } from "@/stores/auth-store";
import {
  useInviteLinkPreview,
  useRedeemInviteLink,
} from "@/hooks/use-invite-links";

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language } = useI18n();
  const { isAuthenticated } = useAuthStore();

  const preview = useInviteLinkPreview(token);
  const redeem = useRedeemInviteLink();
  const [busy, setBusy] = useState(false);

  // If unauthenticated, bounce to login with ?redirect=/invite/:token.
  // The login page picks this up and navigates back here on success.
  useEffect(() => {
    if (!isAuthenticated && token && preview.data?.role === "VIEWER") {
      navigate(`/public-project/${token}`, { replace: true });
    } else if (
      !isAuthenticated &&
      token &&
      preview.data &&
      preview.data.role !== "VIEWER"
    ) {
      navigate(
        `/login?redirect=${encodeURIComponent(`/invite-link/${token}`)}`,
        {
          replace: true,
        },
      );
    }
  }, [isAuthenticated, token, navigate, preview.data]);

  const handleAccept = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const result = await redeem.mutateAsync(token);
      toast({
        title: t("inviteLink.acceptSuccess"),
        description: t("inviteLink.acceptSuccessDescription").replace(
          "{name}",
          preview.data?.projectName ?? "",
        ),
      });
      navigate(`/projects/${result.projectId}`);
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("inviteLink.acceptFailed"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────
  if (preview.isLoading || (!isAuthenticated && preview.data)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
      </div>
    );
  }

  // ── Invalid / revoked / expired link ────────────────────
  if (preview.error || !preview.data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <XCircle className="h-12 w-12 mx-auto mb-3 text-destructive" />
          <h1 className="text-lg font-semibold mb-2">
            {t("inviteLink.invalid")}
          </h1>
          <p className="text-sm text-foreground-muted mb-4">
            {t("inviteLink.invalidDescription")}
          </p>
          <Button asChild variant="outline">
            <Link to="/projects">{t("inviteLink.backToProjects")}</Link>
          </Button>
        </Card>
      </div>
    );
  }

  const data = preview.data;

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <Card className="max-w-md w-full p-8 text-center">
        <div
          className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full text-3xl"
          style={{ backgroundColor: data.projectColor ?? "#6366f1" }}
        >
          <span>{data.projectEmoji ?? "📁"}</span>
        </div>
        <h1 className="text-xl font-semibold mb-1">{t("inviteLink.title")}</h1>
        <p className="text-sm text-foreground-muted mb-4">
          {t("inviteLink.subtitle").replace("{name}", data.projectName)}
        </p>

        <div className="flex items-center justify-center gap-2 mb-4">
          <Badge variant="secondary">
            {t(
              data.role === "MEMBER"
                ? "inviteLink.role.MEMBER"
                : "inviteLink.role.VIEWER",
            )}
          </Badge>
          <span className="text-xs text-foreground-muted">
            {t("inviteLink.invitedBy").replace("{name}", data.invitedBy.name)}
          </span>
        </div>

        {data.expiresAt && (
          <p className="text-xs text-foreground-muted mb-4">
            {t("inviteLink.expiresAt").replace(
              "{date}",
              new Date(data.expiresAt).toLocaleString(),
            )}
          </p>
        )}

        <div className="flex flex-col gap-2">
          {data.role === "VIEWER" && (
            <Button asChild variant="outline">
              <Link to={`/public-project/${token}`}>
                {language === "fr"
                  ? "Voir sans rejoindre"
                  : "View without joining"}
              </Link>
            </Button>
          )}
          <Button onClick={handleAccept} disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            {t("inviteLink.accept")}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/projects">
              <LogIn className="h-4 w-4 mr-2" />
              {t("inviteLink.decline")}
            </Link>
          </Button>
        </div>

        <p className="text-[10px] text-foreground-muted mt-4 flex items-center justify-center gap-1">
          <Sparkles className="h-3 w-3" />
          {t("inviteLink.authenticatedNotice")}
        </p>
      </Card>
    </div>
  );
}
